"use client";

import { useTranslations } from "next-intl";
import { Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LTrustBadge } from "@/components/l-trust/LTrustBadge";
import type { PartnerProfile } from "@/types";
import { supabaseImageVariant } from "@/lib/supabase-image";
import { useState, type ReactNode } from "react";

/** 값 뒤에 붙는 단위(회·개·件…). en 처럼 단위가 없는 로케일은 렌더하지 않는다. */
function Unit({ children }: { children: string }) {
  if (!children) return null;
  return (
    <span className="text-xs font-medium text-gray-500 sm:text-sm">
      {children}
    </span>
  );
}

/** 이 값 이상이면 정확한 건수 대신 "100+" 처럼 자릿수로 내림해 표시한다. */
const ORDER_BUCKET_THRESHOLD = 100;

/**
 * 주문 건수 표시값. 100 미만은 실수치 그대로, 100 이상은 가장 큰 10의
 * 거듭제곱으로 내림한다 (145 → 100, 1534 → 1000).
 *
 * 100 미만을 버킷에 넣지 않는 이유: 63건이 "10+" 로 표시되면 실제보다
 * 작아 보인다. 압축은 숫자가 충분히 클 때만 의미가 있다.
 *
 * 자릿수 계산에 Math.log10 대신 자릿수 길이를 쓰는 건 부동소수점 오차
 * (log10(1000) 이 2.999... 로 나오는 엔진) 를 피하기 위해서다.
 */
function formatOrderCount(count: number): { value: number; approx: boolean } {
  if (count < ORDER_BUCKET_THRESHOLD) return { value: count, approx: false };
  const digits = String(Math.floor(count)).length;
  return { value: 10 ** (digits - 1), approx: true };
}

interface PartnerProfileCardProps {
  partner: PartnerProfile;
  /** 활성 물리 지점 수 — 아래 "지점 안내" 탭 개수와 같은 출처여야 한다. */
  branchCount: number;
}

/**
 * 파트너 공개 프로필 카드. 좌측 신원(로고·이름·L-Trust), 우측 통계 목록.
 *
 * 값이 0인 지표는 렌더하지 않는다 — 전체 파트너 22곳 중 14곳이 주문 0건이라
 * 그대로 두면 "0회"만 잔뜩 노출된다. 통계가 하나도 없으면 우측 열 자체를
 * 생략하고 좌측이 카드 폭을 채운다.
 */
export default function PartnerProfileCard({
  partner,
  branchCount,
}: PartnerProfileCardProps) {
  const t = useTranslations();
  // Logo variant may not exist yet (backfill pending) → remember the URL whose
  // variant failed and fall back to the original object for it.
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const logoUrl = partner.logo_url;
  const logoSrc = logoUrl
    ? failedLogoUrl === logoUrl
      ? logoUrl
      : supabaseImageVariant(logoUrl, 192)
    : undefined;

  const reviewCount = partner.total_reviews ?? 0;
  const orderCount = partner.order_count ?? 0;

  const rows: Array<{ key: string; value: ReactNode; label: string }> = [];

  if (reviewCount > 0) {
    rows.push({
      key: "rating",
      value: (
        <>
          {partner.rating}
          <Star className="size-3.5 self-center fill-orange-500 text-orange-500" />
          <Unit>{`(${reviewCount}${t("partner.profileCard.reviewsUnit")})`}</Unit>
        </>
      ),
      label: t("partner.profileCard.reviewsLabel"),
    });
  }

  if (orderCount > 0) {
    const orders = formatOrderCount(orderCount);
    rows.push({
      key: "orders",
      value: (
        <>
          {/* 숫자와 "+" 를 한 텍스트 노드로: dd 가 flex gap-1 이라
              나누면 "100 +" 로 벌어진다 */}
          {`${t("partner.profileCard.ordersValue", { count: orders.value })}${
            orders.approx ? "+" : ""
          }`}
          <Unit>{t("partner.profileCard.ordersUnit")}</Unit>
        </>
      ),
      label: t("partner.profileCard.ordersLabel", { count: orderCount }),
    });
  }

  if (branchCount > 0) {
    rows.push({
      key: "branches",
      value: (
        <>
          {t("partner.profileCard.branchesValue", { count: branchCount })}
          <Unit>{t("partner.profileCard.branchesUnit")}</Unit>
        </>
      ),
      label: t("partner.profileCard.branchesLabel", { count: branchCount }),
    });
  }

  return (
    <section className="relative z-10 mx-auto w-full max-w-sm rounded-3xl bg-white px-6 py-6 shadow-[0_8px_30px_rgba(0,0,0,0.12)] sm:max-w-md sm:px-8 sm:py-7">
      <div className="flex items-center gap-5 sm:gap-7">
        <div className="flex min-w-0 flex-1 flex-col items-center gap-2 text-center">
          <Avatar className="size-20 border border-gray-100 shadow-sm sm:size-24">
            <AvatarImage
              // Radix AvatarImage is a raw <img> (no next/image loader), so
              // pre-size the logo: size-24 = 96px → 192 → -w480 variant.
              src={logoSrc}
              onLoadingStatusChange={(status) => {
                if (status === "error" && logoUrl && logoSrc !== logoUrl) {
                  setFailedLogoUrl(logoUrl);
                }
              }}
              alt={partner.name}
              className="object-cover"
            />
            <AvatarFallback className="bg-linear-to-br from-orange-400 to-orange-600 text-3xl font-bold text-white">
              {partner.name?.[0]?.toUpperCase() || "P"}
            </AvatarFallback>
          </Avatar>
          <h1 className="max-w-full truncate text-xl font-semibold text-gray-900 sm:text-2xl">
            {partner.name}
          </h1>
          <LTrustBadge score={partner.l_trust_score ?? 29} size="sm" />
        </div>

        {rows.length > 0 && (
          // text-left 명시: 페이지 헤더의 text-center 를 상속하면 안 된다
          <dl className="w-28 shrink-0 divide-y divide-gray-100 text-left sm:w-32">
            {rows.map((row) => (
              <div key={row.key} className="py-2.5 first:pt-0 last:pb-0">
                <dt className="text-[11px] leading-snug text-gray-500 sm:text-xs">
                  {row.label}
                </dt>
                <dd className="mt-0.5 flex items-baseline gap-1 whitespace-nowrap text-xl font-semibold leading-tight text-gray-900 sm:text-2xl">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}
